import * as moment from 'moment';
import { Field } from '../../atoms/forms';
import { rangeOfValues, scaleValueOf, ScaleRange, valueColor } from '../map/colorScale';
import { translateToString } from '../../styles/global/translate';

// How a promotion field's value reads, in the popup's grids, the listing and
// the export: by its type, then through the field's format when it has one.
// A computed field (one with a formula) is read-only everywhere: the backend
// writes its value on every save of the promotion or of its per-product
// fields, and stores it beside the typed-in values under the field's id.

export function isComputedField(field: Field): boolean {
	return !!field.formula?.trim();
}

// `{}` in a format: the value as shown; `{:N}`: a number with N decimals
const PLACEHOLDER = /\{(?::(\d{1,2}))?\}/g;

/**
 * The format applied to a value: `{}` stands for the value as shown, `{:N}`
 * for a number rounded to N decimals ("{:2} €", "{:0} pcs"); a format
 * without a placeholder follows the value. `{:N}` on a value that is not a
 * number shows it as it is.
 */
export function applyFormat(field: Field, shown: string, numeric?: number): string {
	const format = field.format?.trim();
	if (!format || shown === '-') return shown;
	if (!format.match(PLACEHOLDER)) return `${shown} ${format}`;
	return format.replace(PLACEHOLDER, (_, digits: string | undefined) =>
		digits !== undefined && numeric !== undefined && Number.isFinite(numeric) ? numeric.toFixed(Number(digits)) : shown);
}

export function promotionFieldDisplay(field: Field, value: unknown): string {
	if (value === null || value === undefined) return '-';
	let shown: string;
	// the number a `{:N}` placeholder rounds, in currency units for money
	let numeric: number | undefined;
	switch (field.type) {
		case 'Money':
			numeric = Number(value) / 100;
			shown = numeric.toFixed(2);
			break;
		case 'Multiselect':
			shown = Array.isArray(value) ? value.length > 0 ? value.join(', ') : '-' : String(value);
			break;
		case 'Date':
			shown = moment(value as string).format('L LT');
			break;
		case 'Boolean':
			shown = translateToString(value ? 'yes' : 'no');
			break;
		default:
			shown = String(value);
			if (typeof value === 'number') numeric = value;
			else if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) numeric = Number(value);
	}
	return applyFormat(field, shown, numeric);
}

/**
 * The colour of a promotion field's value, from the colours set on the
 * field: a boolean's colour, or the scale's, read over its own stops when
 * they are fixed and else over the values shown beside it in the list.
 */
export function promotionFieldColor(field: Field, value: unknown, shown?: ScaleRange): string | undefined {
	if (!field.display_options || value === null || value === undefined) return undefined;
	return valueColor(field.display_options, field.type, value, shown);
}

/** The bounds of a field's values shown together in a list, for a relative scale. */
export function promotionFieldRange(field: Field, values: unknown[]): ScaleRange | undefined {
	if (!field.display_options?.color_scale) return undefined;
	return rangeOfValues(values.map(v => scaleValueOf(field.type, v)?.value).filter((v): v is number => v !== undefined));
}
